/**
 * Нить состояния соединения — полоса 2 px на нижней границе шапки.
 *
 * Заменила полосу во всю ширину с текстом «Соединение потеряно. Переподключение...», которая
 * стояла В ПОТОКЕ над шапкой и на каждом разрыве сдвигала всю страницу на ~30 px. Решение
 * владельца 26.08.2026: соединение — фоновый процесс приложения, а не сообщение ему; о нём
 * говорят там, где граница уже нарисована, и молча.
 *
 * Нить `absolute` и ложится ПОВЕРХ `border-b` хозяина: она не занимает ни пикселя раскладки,
 * поэтому её появление и уход ничего не двигают — включая шапки кабинета, которые меряют себя
 * `ResizeObserver`'ом и отдают свою высоту спейсеру.
 *
 * **Требование к хозяину: шапка обязана быть позиционированной.** Три собственные шапки кабинета
 * уже `fixed`, общий `TopBar` получил `relative`. В непозиционированной шапке нить уедет к
 * ближайшему предку и это будет видно сразу.
 *
 * Текста на экране нет вовсе — только для скринридера. Живая область рендерится ВСЕГДА, в том
 * числе пустой: область, появляющаяся вместе со своим текстом, объявляется не всеми читалками.
 */

import { useConnectionIndicator, type ConnectionIndicatorState } from '@/realtime/useConnectionIndicator';

// ============================================================================
// Types
// ============================================================================

export interface ConnectionThreadProps {
  /** Состояние берётся пропом, чтобы нить можно было нарисовать и проверить без хаба. */
  state: ConnectionIndicatorState;
}

// ============================================================================
// Component
// ============================================================================

const SPOKEN: Record<ConnectionIndicatorState, string> = {
  idle: '',
  lost: 'Соединение потеряно. Идёт переподключение',
  restored: 'Соединение восстановлено',
};

/**
 * Чистая нить: рисует состояние, ничего о нём не зная.
 *
 * @example
 * ```tsx
 * <ConnectionThread state="lost" />
 * ```
 */
export function ConnectionThread({ state }: ConnectionThreadProps): JSX.Element {
  return (
    <>
      <span className="sr-only" role="status" aria-live="polite">
        {SPOKEN[state]}
      </span>

      {state !== 'idle' && (
        <div
          data-connection-state={state}
          aria-hidden="true"
          className={`
            absolute inset-x-0 -bottom-px h-[2px] overflow-hidden pointer-events-none
            ${state === 'lost'
              ? 'bg-[var(--color-warning-subdued)]'
              : 'bg-[var(--color-success)]'
            }
          `}
        >
          {state === 'lost' && (
            <div className="connection-thread-run h-full w-1/3 bg-[var(--color-warning)] animate-connection-thread" />
          )}
        </div>
      )}
    </>
  );
}

/**
 * Нить, подключённая к хабу уведомлений. Именно её вставляют шапки.
 *
 * @example
 * ```tsx
 * <TopBar underline={<ConnectionStatusThread />} … />
 * ```
 */
export function ConnectionStatusThread(): JSX.Element {
  const state = useConnectionIndicator();

  return <ConnectionThread state={state} />;
}

export default ConnectionStatusThread;
